import { FullStory as FS, init as fullstoryInit } from "@fullstory/browser";
import type { NormalizedCustomerDto } from "helpers/normalizeCustomerDto";
import getClientUniqueId from "helpers/getClientUniqueId";

class FullStoryService {
  static readonly #ORG_ID = "358E1";

  #executeSafely = (fn: () => void): void => {
    const isProductionEnv = import.meta.env.PROD && import.meta.env.MODE === "production";
    if (!isProductionEnv) return;

    try {
      fn();
    }
    catch (error) {
      console.error("FullStory error:", error);
    }
  };

  init = (): void => {
    this.#executeSafely(() => {
      fullstoryInit({ orgId: FullStoryService.#ORG_ID });
    });
  };

  identify = (memberDetails: NormalizedCustomerDto = {}): void => {
    this.#executeSafely(() => {
      if (!memberDetails.id) return;

      FS("setIdentity", {
        uid: memberDetails.id.toString(),
        properties: {
          displayName: `${memberDetails.id || "Visitor"} ${
            memberDetails.firstName || ""
          } ${memberDetails.lastName || ""}`.trim(),
          email: memberDetails.email || "-",
          mobilePhone: memberDetails.phone?.toString() || "-",
          customerID: memberDetails.id?.toString() || "-",
          gender: memberDetails.gender || "-",
          dni: memberDetails.dni || "-",
          customerStatus: memberDetails.status || "-",
          customerType: memberDetails.isRtb ? "RTB" : "FTB",
          deviceId: getClientUniqueId()
        }
      });
    });
  };

  updateUserProperties = (userProperties: Record<string, unknown>): void => {
    this.#executeSafely(() => {
      FS("setProperties", {
        type: "user",
        properties: userProperties
      });
    });
  };

  updatePageProperties = (pageProperties: Record<string, unknown>): void => {
    this.#executeSafely(() => {
      FS("setProperties", {
        type: "page",
        properties: pageProperties
      });
    });
  };

  sendEvent = (
    eventName: string,
    eventPayload: Record<string, unknown> = {}
  ): void => {
    this.#executeSafely(() => {
      FS("trackEvent", {
        name: eventName,
        properties: eventPayload
      });
    });
  };

  stopRecord = (): void => {
    this.#executeSafely(() => {
      FS("shutdown");
    });
  };
}

const FullStory = new FullStoryService();

export default FullStory;
